/**
 * The Gatsby Browser API lets you respond to Gatsby-specific events within the browser, and wrap your page components in additional global components.
 *
 * See: https://www.gatsbyjs.com/docs/reference/config-files/gatsby-browser/
 */

import React from 'react'
import { getSessionPassword } from './src/components/passwordProtect/utils'
import PasswordProtect from './src/components/passwordProtect/PasswordProtect'
import { syncGoogleAnalyticsScript } from './src/utils/googleConsent'
import { usesHomepageLayout } from './src/utils/layoutRoutes'

import { triggerQualtricsSurvey } from './src/utils/qualtrics'
import {
	beginPageContextTracking,
	completePageContextTracking,
	pushEvent
} from './src/utils/tracking'

// Track the latest scheduled emission to prevent stale route updates from firing.
let pageContextRequestId = 0
let pageContextTimer = null
const HOMEPAGE_QUALTRICS_DELAY_MS = 40000
let homepageQualtricsTimer = null
let homepageQualtricsVisitId = 0
let homepageQualtricsTriggeredForVisit = false
// Avoid duplicate pushes when route/title/location have not effectively changed.
let lastPageContextSignature = ''
let latestFrenchRequestTitle = ''
let latestFrenchRequestTitlePathname = ''
const TITLE_SYNC_DELAY_MS = 50
const TITLE_SYNC_MAX_RETRIES = 20 // ~1s of grace period, covers async-loaded titles
const GENERIC_REQUEST_TITLES = new Set([
	'nouvelle_requete',
	'creer_une_requete',
	'creer_un_requete',
	'new_request',
	'chargement_de_la_demande'
])

// -----------------------------------------------------------------------------
// React Helmet bridge
//
// Instead of polling the DOM (h1, h1 span, etc.) to guess when the real page
// title has been rendered, we listen for the exact moment React Helmet commits
// a new <title> to the document. The PageTitle component must call
// onChangeClientState on its <Helmet> and dispatch the 'mondossier:title-ready'
// CustomEvent, e.g.:
//
//   <Helmet onChangeClientState={(newState) => {
//     window.dispatchEvent(new CustomEvent('mondossier:title-ready', {
//       detail: { title: newState.title }
//     }))
//   }}>
//     <title>{title}</title>
//   </Helmet>
//
// This gives us an explicit signal instead of a timing guess, and removes the
// need to read business labels out of h1/h1 span text content.
// -----------------------------------------------------------------------------
let latestReadyTitle = typeof document !== 'undefined' ? document.title : ''

if (typeof window !== 'undefined') {
	window.addEventListener('mondossier:title-ready', (event) => {
		latestReadyTitle = event.detail?.title || document.title || ''
	})
	window.addEventListener('mondossier:french-request-title-ready', (event) => {
		const eventPathname = normalizePathname(event.detail?.pathname)
		const currentPathname = normalizePathname(window.location.pathname)

		if (eventPathname === currentPathname) {
			latestFrenchRequestTitle = event.detail?.title || ''
			latestFrenchRequestTitlePathname = eventPathname
		}
	})
}

const isCreateRequestPath = (pathname) => {
	const normalizedPath = normalizePathname(pathname)
	return /^\/auth\/requests\/create(?:\/|$)/.test(normalizedPath)
}

const isRequestDetailPath = (pathname) => {
	const normalizedPath = normalizePathname(pathname)
	return (
		/^\/auth\/requests\/[^/]+\/?$/.test(normalizedPath) &&
		normalizedPath !== '/auth/requests'
	)
}

const isGenericRequestTitle = (value) => {
	return GENERIC_REQUEST_TITLES.has(normalizeTrackingToken(value))
}

// Normalize html lang into the two values expected by the tracking spec.
const normalizeLanguage = () => {
	const lang = (document.documentElement.lang || 'fr').toLowerCase().slice(0, 2)
	return lang === 'en' ? 'en' : 'fr'
}

// Remove query/hash and trailing slashes to keep page naming consistent.
const normalizePathname = (pathname) => {
	if (!pathname) return '/'

	const cleanPath = pathname.split('?')[0].split('#')[0].replace(/\/+$/, '')

	return cleanPath === '' ? '/' : cleanPath
}

const normalizeTrackingToken = (value) => {
	const normalizedValue = (value || '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')

	return normalizedValue
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '')
}

const isTechnicalIdSegment = (segment) => {
	if (!segment) return false

	const value = segment.toLowerCase()
	const isLongHexLikeToken = value.length >= 20 && /^[0-9a-f_-]+$/.test(value)

	return (
		/^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/.test(value) ||
		/^[0-9a-f]{8}(?:_[0-9a-f]{4}){3}_[0-9a-f]{12}$/.test(value) ||
		/^[0-9]{6,}$/.test(value) ||
		isLongHexLikeToken
	)
}

const sanitizePageTitle = (pageTitle) => {
	return (pageTitle || '').trim().replace(/[\s\-_:|]+$/g, '')
}

// Extract a business label from the resolved <title> value (e.g. "MonDossier - Demande pour un nouveau bac"
// -> "demande_pour_un_nouveau_bac"). No more DOM heuristics: the title IS the source of truth now.
const extractBusinessLabelFromTitle = (pageTitle) => {
	if (!pageTitle) {
		return ''
	}

	const titleWithoutTrailingSeparators = pageTitle
		.trim()
		.replace(/[\s\-_:|]+$/g, '')

	const titleParts = titleWithoutTrailingSeparators
		.split(/\s[\-|:]\s|\s\|\s/g)
		.map((part) => part.trim())
		.filter(Boolean)

	const candidate =
		titleParts.length > 1
			? titleParts[titleParts.length - 1]
			: titleWithoutTrailingSeparators

	const normalizedCandidate = normalizeTrackingToken(candidate)

	if (!normalizedCandidate || GENERIC_REQUEST_TITLES.has(normalizedCandidate)) {
		return ''
	}

	return normalizedCandidate
}

const resolveBusinessLabel = (pageTitle) => {
	return extractBusinessLabelFromTitle(pageTitle)
}

const resolveTrackingPageTitle = (rawPageTitle) => {
	return sanitizePageTitle(rawPageTitle)
}

const finalizePageName = (language, domainSegments) => {
	const segments = domainSegments
		.map((segment) => normalizeTrackingToken(segment))
		.filter(Boolean)
		.filter((segment) => !isTechnicalIdSegment(segment))
		.filter(
			(segment, index, list) => index === 0 || segment !== list[index - 1]
		)

	const fallbackSegments = segments.length > 0 ? segments : ['home']
	return `MonDossier_laval:${language}:${fallbackSegments.join(':')}`.replace(
		/[:_-]+$/g,
		''
	)
}

const isCreateRequestWithGroupParam = (search) => {
	const params = new URLSearchParams(search || '')
	return params.has('group')
}

// Build a stable page_name value from path segments for cross-page reporting.
const buildStablePageName = (pathname, language, pageTitle, search) => {
	const normalizedPath = normalizePathname(pathname)
	const rawSegments = normalizedPath
		.replace(/^\//, '')
		.split('/')
		.filter(Boolean)

	if (rawSegments.length === 0) {
		return `MonDossier_laval:${language}:home`
	}

	const section = rawSegments[0]?.toLowerCase()
	const resource = rawSegments[1]?.toLowerCase()
	const action = rawSegments[2]?.toLowerCase()
	const requestIdSegment = rawSegments[3]
	const labelFromTitle = resolveBusinessLabel(pageTitle)

	if (section === 'auth' && resource === 'requests' && action === 'create') {
		const hasTechnicalIdSegment = isTechnicalIdSegment(requestIdSegment)
		const hasGroupParam = isCreateRequestWithGroupParam(search)

		const createRequestLabel = !hasGroupParam
			? 'nouvelle_requete'
			: 'creer_requete'

		const businessLabel =
			language === 'en' && !hasGroupParam
				? normalizeTrackingToken(
						latestFrenchRequestTitlePathname === normalizePathname(pathname)
							? latestFrenchRequestTitle
							: ''
				  )
				: labelFromTitle

		return finalizePageName(language, [
			createRequestLabel,
			businessLabel || 'request_creation'
		])
	}

	if (section === 'auth' && resource === 'profile') {
		if (action === 'preferences') {
			return finalizePageName(language, ['profile', 'preferences'])
		}

		return finalizePageName(language, ['profile', 'overview'])
	}

	if (section === 'auth' && resource === 'requests') {
		if (rawSegments.length === 2) {
			return finalizePageName(language, ['my_requests', 'list'])
		}

		const requestId = normalizeTrackingToken(rawSegments[2])
		return `MonDossier_laval:${language}:request_detail:${requestId}`
	}

	if (section === 'auth' && resource === 'payment') {
		return finalizePageName(language, ['payment', 'overview'])
	}

	const normalizedSegments = rawSegments
		.map((segment) => normalizeTrackingToken(segment))
		.filter(Boolean)
		.filter((segment) => !isTechnicalIdSegment(segment))

	return finalizePageName(language, normalizedSegments)
}

// Detect 404 pages to enrich page_context_ready with error metadata.
const isErrorPage = (pathname, title) => {
	const normalizedPath = normalizePathname(pathname)
	const normalizedTitle = (title || '').toLowerCase()

	return (
		normalizedPath === '/404' ||
		normalizedPath === '/404.html' ||
		normalizedTitle.includes('404') ||
		normalizedTitle.includes('not found') ||
		normalizedTitle.includes('introuvable')
	)
}

// Detect event pages so GTM can classify page views by category.
const isEventPage = (pathname) => {
	const normalizedPath = normalizePathname(pathname)

	return (
		normalizedPath.includes('/evenement') || normalizedPath.includes('/event')
	)
}

// Build the canonical payload pushed with page_context_ready.
const buildPageContextPayload = (pathname, search) => {
	const language = normalizeLanguage()
	const pageTitle = resolveTrackingPageTitle(latestReadyTitle || document.title)
	const errorPage = isErrorPage(pathname, pageTitle)

	const payload = {
		page_name: buildStablePageName(pathname, language, pageTitle, search),
		page_title: pageTitle,
		page_type: errorPage ? 'error' : 'standard',
		page_category: isEventPage(pathname) ? 'evenement' : 'standard',
		page_location: window.location.href,
		hostname: window.location.hostname,
		language
	}

	if (errorPage) {
		payload.error_type = 'not_found'
		payload.error_message = pageTitle || '404_not_found'
	}

	return payload
}

const pushPageContextReady = (pathname, search) => {
	const payload = buildPageContextPayload(pathname, search)
	const signature = `${payload.page_name}|${payload.page_title}|${payload.page_location}`

	// Ignore strictly identical payloads to keep dataLayer noise low.
	if (signature === lastPageContextSignature) {
		completePageContextTracking()
		return
	}

	lastPageContextSignature = signature
	pushEvent('page_context_ready', payload)
}

// Wait for React Helmet's onChangeClientState signal (via 'mondossier:title-ready')
// instead of polling document.title/h1 text. We still keep a bounded retry loop as a
// safety net in case a page never fires the event (e.g. static title, no PageTitle usage).
const schedulePageContextReady = (pathname, search = '') => {
	if (typeof window === 'undefined') {
		return
	}

	pageContextRequestId += 1
	const requestId = pageContextRequestId
	const titleAtScheduleTime = latestReadyTitle
	const needsBusinessLabel =
		isCreateRequestPath(pathname) || isRequestDetailPath(pathname)
	const needsFrenchRequestTitle =
		normalizeLanguage() === 'en' && isCreateRequestPath(pathname)
	const hasFrenchRequestTitle =
		latestFrenchRequestTitlePathname === normalizePathname(pathname) &&
		Boolean(latestFrenchRequestTitle)
	let retries = 0

	if (pageContextTimer) {
		clearTimeout(pageContextTimer)
	}

	const emitWhenTitleSettles = () => {
		// Stop if a newer navigation has already been scheduled.
		if (requestId !== pageContextRequestId) {
			return
		}

		const currentTitle = latestReadyTitle || document.title
		const titleHasUpdated = currentTitle !== titleAtScheduleTime
		const currentTitleIsGeneric = isGenericRequestTitle(
			sanitizePageTitle(currentTitle)
		)
		const reachedRetryLimit = retries >= TITLE_SYNC_MAX_RETRIES

		// For pages that must carry a real business label (e.g. /auth/requests/create),
		// only settle once the title changed AND is no longer a generic placeholder.
		const isSettled = needsBusinessLabel
			? titleHasUpdated &&
			  !currentTitleIsGeneric &&
			  (!needsFrenchRequestTitle || hasFrenchRequestTitle)
			: titleHasUpdated || currentTitle

		if (isSettled || reachedRetryLimit) {
			pushPageContextReady(pathname, search)
			return
		}

		retries += 1
		pageContextTimer = setTimeout(emitWhenTitleSettles, TITLE_SYNC_DELAY_MS)
	}

	pageContextTimer = setTimeout(emitWhenTitleSettles, 0)
}

export const wrapPageElement = () => {
	const password = process.env.PASSWORD_PROTECT

	// password protection disabled if PROD
	if (process.env.PRODUCTION === true || process.env.PRODUCTION === 'true') {
		return
	}

	// check password
	const passwordCandidate = getSessionPassword()
	if (passwordCandidate === password) {
		return
	}

	// ask for password
	return <PasswordProtect />
}

const scheduleQualtricsForPage = (pathname) => {
	if (!usesHomepageLayout(pathname)) {
		homepageQualtricsVisitId += 1
		clearTimeout(homepageQualtricsTimer)
		homepageQualtricsTimer = null
		homepageQualtricsTriggeredForVisit = false
		return
	}

	if (homepageQualtricsTimer || homepageQualtricsTriggeredForVisit) {
		return
	}

	homepageQualtricsVisitId += 1
	const visitId = homepageQualtricsVisitId

	homepageQualtricsTimer = setTimeout(() => {
		homepageQualtricsTimer = null
		if (visitId !== homepageQualtricsVisitId) {
			return
		}

		homepageQualtricsTriggeredForVisit = true
		void triggerQualtricsSurvey({
			shouldRun: () =>
				visitId === homepageQualtricsVisitId &&
				usesHomepageLayout(window.location.pathname)
		})
	}, HOMEPAGE_QUALTRICS_DELAY_MS)
}

// Gatsby browser entry point: initialize GA and emit first page_context_ready.
export const onClientEntry = () => {
	beginPageContextTracking()
	// Sync GA with the latest consent state.
	syncGoogleAnalyticsScript()
	scheduleQualtricsForPage(window.location.pathname)

	// Push initial page_context_ready event
	if (typeof window !== 'undefined') {
		schedulePageContextReady(window.location.pathname, window.location.search)
	}

	return
}

// Close the ordering gate before Gatsby renders the next route.
export const onPreRouteUpdate = () => {
	beginPageContextTracking()
}

/**
 * Track page context ready on SPA navigation (route changes)
 * Fires every time user navigates within the site
 */
export const onRouteUpdate = ({ location, prevLocation }) => {
	if (prevLocation === null) {
		// Skip first navigation (already handled by onClientEntry)
		return
	}

	scheduleQualtricsForPage(location.pathname)

	// Track SPA navigation
	schedulePageContextReady(location.pathname, location.search)
}
